import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { diccionarioDe, type Idioma } from '../../i18n/index.js';
import { VERSION_TERMINOS, textoDeTerminos } from '../estaticas/legal.js';
import { useSesion } from '../../common/hooks/useSesion.js';
import { CONSENTIMIENTOS_CAMBIADOS, descartarConsentimientoPendiente } from './consentimientosPendientes.js';
import { guardarConsentimiento, obtenerPerfil, type PerfilDeCuenta } from '../../common/services/cuentas.js';

/**
 * ADMIN-02, reconsentimiento por versión (2026-09-29, pendiente anotado en `plan/decisiones.md` el
 * 2026-09-28, condición: antes de abrir el registro al público).
 *
 * Un aviso simple, no bloqueante: quien tiene sesión y aceptó una versión de los términos que ya no
 * es la vigente (o nunca los aceptó) lo ve en cualquier página, puede seguir leyendo Athena con
 * normalidad, y solo pierde la cuota de su cuenta (`cuotasPorNivel.ts` la trata como anónima)
 * mientras no pulse "Aceptar". Sin pantalla nueva: reusa `/terminos`, que ya existe.
 */
export function AvisoReconsentimiento({ idioma }: { readonly idioma: Idioma }) {
  const t = diccionarioDe(idioma).cuentas.reconsentimiento;
  const sesion = useSesion();
  const tokenActual = useRef(sesion?.access_token);
  tokenActual.current = sesion?.access_token;
  const [perfil, setPerfil] = useState<PerfilDeCuenta | null | undefined>(undefined);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPerfil(undefined);
    setError(false);
    setGuardando(false);
    if (sesion === null || sesion === undefined) return;
    let vigente = true;
    void obtenerPerfil(sesion.access_token).then(p => {
      if (vigente) setPerfil(p);
    });
    return () => {
      vigente = false;
    };
  }, [sesion]);

  if (sesion === null || sesion === undefined || perfil === null || perfil === undefined || !perfil.terminosDesactualizados) return null;

  const aceptar = () => {
    setGuardando(true);
    setError(false);
    void guardarConsentimiento(
      { tipo: 'terminos', aceptado: true, textoVersion: VERSION_TERMINOS, texto: textoDeTerminos(idioma) },
      sesion.access_token,
    ).then(guardado => {
      if (tokenActual.current !== sesion.access_token) return;
      setGuardando(false);
      if (guardado) {
        if (sesion.user.email) descartarConsentimientoPendiente(sesion.user.email, 'terminos');
        setPerfil(p => (p === null || p === undefined ? p : { ...p, terminosDesactualizados: false }));
        window.dispatchEvent(new Event(CONSENTIMIENTOS_CAMBIADOS));
      } else setError(true);
    });
  };

  return (
    <div role='status' className='flex flex-wrap items-center justify-center gap-3 bg-(--chat-bg) px-4 py-3 text-center'>
      <p className='m-0 font-(family-name:--text) text-[13px] text-(--ink-soft)'>
        {perfil.consentimientos.terminos ? t.texto : t.primeraAceptacion}{' '}
        <Link to='/terminos' target='_blank' className='text-(--teal-deep) underline'>
          {t.enlace}
        </Link>
      </p>
      <button
        type='button'
        onClick={aceptar}
        disabled={guardando}
        className='sobre-teal cursor-pointer rounded-full bg-(--teal-deep) px-4 py-1.5 font-(family-name:--text) text-[13px] disabled:cursor-not-allowed disabled:opacity-40'
      >
        {guardando ? t.aceptando : t.aceptar}
      </button>
      {error ? <p role='alert'>{t.error}</p> : null}
    </div>
  );
}
